import { ArrowPathIcon, BackwardIcon, ForwardIcon, PauseIcon, PlayIcon } from "@heroicons/react/24/outline";
import { frameDurationSeconds } from "@/components/audits/replay/constants";
import { timelinePercentage } from "@/components/audits/replay/helpers";
import { formatDuration } from "@/utils/formatters";

type FindingMark = {
  id: string;
  count: number;
};

type Props = {
  screenshotCount: number;
  currentTime: number;
  playing: boolean;
  findingMarks: FindingMark[];
  onSeek: (time: number, options?: { play?: boolean }) => void;
  onTogglePlayback: () => void;
};

export default function LiveEvaluationReplayControls({
  screenshotCount,
  currentTime,
  playing,
  findingMarks,
  onSeek,
  onTogglePlayback,
}: Props) {
  if (screenshotCount === 0) {
    return null;
  }

  const lastFrame = Math.max(screenshotCount - 1, 0);
  const totalDuration = lastFrame * frameDurationSeconds;
  const clampedTime = Math.min(Math.max(currentTime, 0), totalDuration);
  const displayPercent = totalDuration === 0 ? 0 : (clampedTime / totalDuration) * 100;
  const currentFrame = Math.min(Math.floor(clampedTime / frameDurationSeconds), lastFrame);
  const atEnd = screenshotCount > 1 && !playing && clampedTime >= totalDuration;
  const seekToFrame = (frame: number, options?: { play?: boolean }) => {
    onSeek(Math.min(Math.max(frame, 0), lastFrame) * frameDurationSeconds, options);
  };

  return (
    <div className="border-t border-gray-200 bg-gray-50 px-4 py-3 sm:px-5">
      <div role="toolbar" aria-label="Replay timeline" className="relative mb-3 h-2">
        <button
          type="button"
          className="absolute inset-0 cursor-pointer rounded-full bg-gray-200"
          aria-label="Seek replay"
          onClick={(event) => {
            const rect = event.currentTarget.getBoundingClientRect();
            const percentage = (event.clientX - rect.left) / rect.width;
            seekToFrame(Math.round(Math.min(Math.max(percentage, 0), 1) * lastFrame));
          }}
        >
          <span
            className="absolute left-0 top-0 h-full rounded-full bg-primary"
            style={{ width: `${displayPercent}%` }}
          />
        </button>
        {findingMarks.map((mark, index) => {
          if (mark.count === 0) return null;

          const label = mark.count === 1 ? "1 issue" : `${mark.count} issues`;

          return (
            <button
              key={mark.id}
              type="button"
              className="group absolute top-1/2 z-10 h-8 w-8 -translate-x-1/2 -translate-y-1/2 cursor-pointer"
              style={{ left: `${timelinePercentage(index, screenshotCount)}%` }}
              aria-label={label}
              onClick={() => seekToFrame(index)}
            >
              <span className="absolute inset-0 rounded-full bg-transparent group-hover:bg-warning/20" />
              <span className="absolute inset-1 rounded-full border-2 border-warning opacity-0 group-hover:opacity-100" />
              <span className="absolute left-1/2 top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full bg-warning" />
              <span className="pointer-events-none absolute bottom-full left-1/2 mb-2 hidden -translate-x-1/2 whitespace-nowrap rounded bg-black/80 px-3 py-2 text-xs text-white group-hover:block">
                {label}
              </span>
            </button>
          );
        })}
      </div>

      <div className="relative flex items-center justify-center">
        <div className="absolute left-0 text-sm text-gray-500">{formatDuration(clampedTime)}</div>
        <div className="absolute right-0 text-sm text-gray-500">{formatDuration(totalDuration)}</div>
        <div className="relative flex items-center gap-3 sm:gap-4">
          <button
            type="button"
            className="absolute right-full mr-3 hidden cursor-pointer text-gray-500 transition-colors hover:text-gray-800 lg:block"
            aria-label="Restart"
            onClick={() => seekToFrame(0, { play: screenshotCount > 1 })}
          >
            <ArrowPathIcon className="h-5 w-5" aria-hidden="true" />
          </button>
          <button
            type="button"
            className="cursor-pointer text-gray-500 transition-colors hover:text-gray-800"
            aria-label="Previous frame"
            onClick={() => seekToFrame(currentFrame - 1)}
          >
            <BackwardIcon className="h-5 w-5" aria-hidden="true" />
          </button>
          <button
            type="button"
            className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full bg-primary text-white shadow-sm transition-colors hover:bg-primary/90"
            onClick={onTogglePlayback}
            aria-label={playing ? "Pause" : atEnd ? "Restart" : "Play"}
          >
            {playing ? (
              <PauseIcon className="h-5 w-5" aria-hidden="true" />
            ) : atEnd ? (
              <ArrowPathIcon className="h-5 w-5" aria-hidden="true" />
            ) : (
              <PlayIcon className="ml-0.5 h-5 w-5" aria-hidden="true" />
            )}
          </button>
          <button
            type="button"
            className="cursor-pointer text-gray-500 transition-colors hover:text-gray-800"
            aria-label="Next frame"
            onClick={() => seekToFrame(currentFrame + 1)}
          >
            <ForwardIcon className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>
      </div>
    </div>
  );
}
